import { useSelector } from 'react-redux'
import { SupportedBrands } from 'src/enums'
import { selectBrandName } from 'src/redux/selectors/session.selectors'

type UseWhiteLabelHelpers = {
  /**
   * Returns a boolean if the current whitelabel application is **included** as a parameter.
   * Use this utility to **show** specific whitelabeled content to the end user
   * @param brand {SupportedBrands | SupportedBrands[]} A `SupportedBrands` enum, singular or in an array
   * @returns boolean
   */
  whiteLabelIncludes: (brand: SupportedBrands | SupportedBrands[]) => boolean

  /**
   * Returns a boolean if the current whitelabel application is **excluded** as a parameter.
   * Use this utility to **hide** specific whitelabeled content to the end user
   * @param brand {SupportedBrands | SupportedBrands[]} A `SupportedBrands` enum, singular or in an array
   * @returns boolean
   */
  whiteLabelExcludes: (brand: SupportedBrands | SupportedBrands[]) => boolean
}

/**
 * Allows a React component to use whitelabeling utilities to include or exclude elements/logic
 * depending on which whitelabel brand is using the application
 * @returns UseWhiteLabelHelpers
 */
export function useWhitelabel(): UseWhiteLabelHelpers {
  const brandName = useSelector(selectBrandName) as SupportedBrands

  return {
    whiteLabelIncludes(brand: SupportedBrands | SupportedBrands[]): boolean {
      if (Array.isArray(brand)) {
        return brand.includes(brandName)
      }

      return brandName === brand
    },
    whiteLabelExcludes(brand: SupportedBrands | SupportedBrands[]): boolean {
      if (Array.isArray(brand)) {
        return !brand.includes(brandName)
      }

      return brandName !== brand
    },
  }
}
